/**
 * Shared utility functions for source indicators
 * Used in both ExtractItems2 and TopicTableEditor for consistent indicator display
 */

// Current document ID for source indicator logic
// This should match the current document being viewed
export const DEFAULT_CURRENT_DOCUMENT_ID = 'a8f9e0b1-c2d3-4567-1234-890123456789';

export type SourceInfo = {
  sourceType: string;
  sourceID?: string;
  sourceName?: string;
  sourceDate?: string;
};

/**
 * Get source indicator letter based on sourceInfo
 */
export function getSourceIndicator(
  sourceInfo: SourceInfo | null | undefined,
  currentDocumentID: string = DEFAULT_CURRENT_DOCUMENT_ID
): string {
  if (!sourceInfo) return '';
  
  // If sourceType is "system" and sourceID matches currentDocumentID, show "D"
  if (sourceInfo.sourceType === 'system' && sourceInfo.sourceID === currentDocumentID) {
    return 'D';
  }
  
  // If sourceType is "user", show "U"
  if (sourceInfo.sourceType === 'user') {
    return 'U';
  }
  
  // If sourceType is "system" (and sourceID doesn't match), show "A"
  if (sourceInfo.sourceType === 'system') {
    return 'A';
  }
  
  return '';
}

/**
 * Format date for tooltip display
 */
export function formatDate(dateString: string): string {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const hoursStr = hours.toString().padStart(2, '0');
    
    return `${day}-${month}-${year} ${hoursStr}:${minutes} ${ampm}`;
  } catch (e) {
    return dateString;
  }
}

/**
 * Create DOM element for cell with source indicator
 * Returns HTMLElement for AG Grid cellRenderer compatibility
 * Uses consistent colors: Purple (#9333ea) for A, Orange (#d97706) for U, Green (#22c55e) for D
 */
export function createCellWithIndicator(
  value: string,
  sourceInfo: SourceInfo | null | undefined,
  currentDocumentID: string = DEFAULT_CURRENT_DOCUMENT_ID
): HTMLElement | string {
  // Only show indicator if value is not empty
  if (!value || value === '' || value === '-') {
    return value || '-';
  }
  
  const indicator = getSourceIndicator(sourceInfo, currentDocumentID);
  const displayValue = value || '-';
  
  if (!indicator) {
    return displayValue;
  }
  
  // Determine CSS class based on indicator
  let indicatorClass = '';
  if (indicator === 'A') {
    indicatorClass = 'source-indicator-a';
  } else if (indicator === 'U') {
    indicatorClass = 'source-indicator-u';
  } else if (indicator === 'D') {
    indicatorClass = 'source-indicator-d';
  }
  
  // Create a DOM element to hold both the value and indicator
  const container = document.createElement('div');
  container.style.display = 'inline-flex';
  container.style.alignItems = 'flex-start';
  container.style.gap = '0.25rem';
  
  const valueSpan = document.createElement('span');
  valueSpan.textContent = displayValue;
  container.appendChild(valueSpan);
  
  const indicatorSpan = document.createElement('span');
  indicatorSpan.className = `source-indicator ${indicatorClass}`;
  indicatorSpan.textContent = indicator;
  
  // Add tooltips for indicators
  let tooltipText = '';
  let shouldShowTooltip = false;
  
  if (indicator === 'U' && sourceInfo && (sourceInfo.sourceName || sourceInfo.sourceDate)) {
    // Tooltip for "U" indicators
    shouldShowTooltip = true;
    if (sourceInfo.sourceName) {
      tooltipText += sourceInfo.sourceName;
    }
    if (sourceInfo.sourceDate) {
      if (tooltipText) tooltipText += '\n';
      tooltipText += `Date: ${formatDate(sourceInfo.sourceDate)}`;
    }
  } else if (indicator === 'D' && sourceInfo && (sourceInfo.sourceName || sourceInfo.sourceDate)) {
    // Tooltip for "D" indicators
    shouldShowTooltip = true;
    if (sourceInfo.sourceName) {
      tooltipText = sourceInfo.sourceName;
    }
    if (sourceInfo.sourceDate) {
      if (tooltipText) tooltipText += '\n';
      tooltipText += `Date: ${formatDate(sourceInfo.sourceDate)}`;
    }
  } else if (indicator === 'A' && sourceInfo && (sourceInfo.sourceName || sourceInfo.sourceDate)) {
    // Tooltip for "A" indicators
    shouldShowTooltip = true;
    if (sourceInfo.sourceName) {
      tooltipText = sourceInfo.sourceName;
    }
    if (sourceInfo.sourceDate) {
      if (tooltipText) tooltipText += '\n';
      tooltipText += `Date: ${formatDate(sourceInfo.sourceDate)}`;
    }
  }
  
  if (shouldShowTooltip && tooltipText) {
    indicatorSpan.classList.add('source-indicator-with-tooltip');
    // Don't set title or data-tooltip attributes - we're using custom tooltip to avoid conflicts with Flowbite/native tooltips
    
    // Position tooltip below the indicator on hover
    let tooltipEl: HTMLElement | null = null;
    indicatorSpan.addEventListener('mouseenter', (e) => {
      if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.className = 'source-tooltip';
        tooltipEl.textContent = tooltipText;
        document.body.appendChild(tooltipEl);
        
        // Position tooltip below the indicator
        const rect = indicatorSpan.getBoundingClientRect();
        tooltipEl.style.top = `${rect.bottom + 4}px`;
        tooltipEl.style.left = `${rect.left + (rect.width / 2)}px`;
        tooltipEl.style.transform = 'translateX(-50%)';
      }
    });
    
    indicatorSpan.addEventListener('mouseleave', () => {
      if (tooltipEl) {
        tooltipEl.remove();
        tooltipEl = null;
      }
    });
    
    // Also handle mouse move to update position if tooltip is visible
    indicatorSpan.addEventListener('mousemove', () => {
      if (tooltipEl) {
        const rect = indicatorSpan.getBoundingClientRect();
        tooltipEl.style.top = `${rect.bottom + 4}px`;
        tooltipEl.style.left = `${rect.left + (rect.width / 2)}px`;
      }
    });
  }
  
  container.appendChild(indicatorSpan);
  
  return container;
}

